import { RequiredMark } from "../ui/RequiredMark.js";
import { useEffect, useMemo, useState } from "react";
import type { LivePresenceEntry, LiveSurface, Member } from "@cs2/contracts";
import { Mail, ShieldCheck, UserRound } from "lucide-react";
import { inviteMember, listMembers, updateMember } from "../api/client.js";
import { statusOf } from "../api/ApiError.js";
import { Badge, CloseButton, Dialog, useNotification } from "../ui/index.js";
import "./members-dialog.css";

function describe(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message.toUpperCase() : fallback;
}

/** Feste Reihenfolge, damit dieselbe Kombination immer gleich aussieht. */
const surfaceOrder: LiveSurface[] = ["quickview", "editor"];
const surfaceLabels: Record<LiveSurface, string> = {
  quickview: "QUICKVIEW",
  editor: "EDITOR",
};

function presenceLabel(
  entry: LivePresenceEntry | undefined,
  presenceConnected: boolean,
): string {
  if (entry)
    return `ONLINE · ${surfaceOrder
      .filter((surface) => entry.surfaces.includes(surface))
      .map((surface) => surfaceLabels[surface])
      .join(" + ")}`;
  // Ohne eigene Live-Verbindung wissen wir über die anderen nichts — dann wäre
  // "OFFLINE" eine Behauptung statt einer Beobachtung.
  return presenceConnected ? "OFFLINE" : "UNBEKANNT";
}

const roleLabels: Record<Member["role"], string> = {
  viewer: "VIEWER",
  editor: "EDITOR",
  captain: "CAPTAIN",
};

/**
 * Mitgliederliste des Teams mit Online-Status — für jede Rolle erreichbar, aus
 * dem Kontomenü von Quickview und Editor. Captains sehen zusätzlich die
 * Verwaltung: Einladungen laufen serverseitig über einen Recovery-Link, hier
 * wird deshalb nie ein Passwort erfasst.
 */
export function MembersDialog({
  csrfToken,
  currentUserId,
  canManage,
  online,
  presenceConnected,
  onClose,
  onAccessLost,
}: {
  csrfToken: string | null;
  currentUserId: string | null;
  /** Nur Captains dürfen einladen, Rollen ändern und Zugänge deaktivieren. */
  canManage: boolean;
  online: LivePresenceEntry[];
  /** Läuft die eigene Live-Verbindung? Sonst ist jede Offline-Aussage geraten. */
  presenceConnected: boolean;
  onClose: () => void;
  /**
   * Ernennt der aktuelle Captain hier ein anderes Mitglied zum Captain, stuft
   * der Server ihn selbst im selben Request auf `editor` zurück (genau ein
   * Captain pro Team). Der Dialog verliert dadurch seinen eigenen
   * Zugriff — `onAccessLost` schließt ihn und lädt die Session neu, statt
   * dass die restliche UI (Captain-only-Buttons, Session-Rolle) veraltet
   * stehen bleibt.
   */
  onAccessLost: () => void;
}) {
  const { notify } = useNotification();
  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Member["role"]>("editor");
  const [status, setStatus] = useState("LÄDT …");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listMembers()
      .then((loaded) => {
        if (!active) return;
        setMembers(loaded);
        setStatus("");
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus("");
        notify({
          message: describe(error, "MITGLIEDER NICHT LADBAR"),
          kind: "error",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  const presenceByUserId = useMemo(
    () => new Map(online.map((entry) => [entry.userId, entry])),
    [online],
  );

  /** Online zuerst — wer gerade da ist, ist die interessantere Information. */
  const rows = useMemo(
    () =>
      [...members].sort((left, right) => {
        const leftOnline = presenceByUserId.has(left.userId) ? 0 : 1;
        const rightOnline = presenceByUserId.has(right.userId) ? 0 : 1;
        if (leftOnline !== rightOnline) return leftOnline - rightOnline;
        return left.email.localeCompare(right.email);
      }),
    [members, presenceByUserId],
  );

  /**
   * Lädt die Mitgliederliste neu — nötig nach jeder Rollenänderung, weil eine
   * Captain-Ernennung serverseitig automatisch den bisherigen Captain
   * zurückstuft (genau ein Captain pro Team); ein einfacher Merge der
   * geänderten Zeile würde die andere veraltet lassen. War der aktuelle
   * Nutzer selbst der zurückgestufte Captain, schlägt dieser Reload nicht
   * fehl (die Liste darf jedes Mitglied lesen), aber seine Verwaltungsrechte
   * sind weg — das erkennt der Aufrufer an der neu geladenen Session.
   */
  async function reloadMembers(): Promise<void> {
    try {
      setMembers(await listMembers());
    } catch (error) {
      if (statusOf(error) === 403) {
        onAccessLost();
        return;
      }
      notify({
        message: describe(error, "MITGLIEDERLISTE NICHT AKTUALISIERBAR"),
        kind: "error",
      });
    }
  }

  async function invite() {
    if (!csrfToken) return;
    setBusy(true);
    setStatus("EINLADUNG WIRD VERSENDET …");
    try {
      const created = await inviteMember(email.trim(), role, csrfToken);
      setEmail("");
      notify({
        message: `EINLADUNG AN ${created.email.toUpperCase()} VERSENDET`,
        kind: "success",
      });
      await reloadMembers();
    } catch (error) {
      notify({
        message: describe(error, "EINLADUNG FEHLGESCHLAGEN"),
        kind: "error",
      });
    } finally {
      setStatus("");
      setBusy(false);
    }
  }

  async function patch(
    member: Member,
    change: { role?: Member["role"]; active?: boolean },
  ) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      const updated = await updateMember(member.userId, change, csrfToken);
      notify({ message: "GESPEICHERT", kind: "success" });
      await reloadMembers();
      // Hat der Captain jemand anderen befördert, ist er selbst jetzt Editor.
      if (updated.role === "captain" && member.userId !== currentUserId)
        onAccessLost();
    } catch (error) {
      notify({
        message: describe(error, "ÄNDERUNG FEHLGESCHLAGEN"),
        kind: "error",
      });
    } finally {
      setStatus("");
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="MITGLIEDER"
      contentClassName="ui-modal-card members-dialog"
    >
      <header>
        <h2>
          <UserRound /> MITGLIEDER
        </h2>
        <CloseButton onClick={onClose} />
      </header>
      {presenceConnected ? null : (
        <p className="members-presence-warning">
          PRÄSENZ NICHT VERBUNDEN — ONLINE-STATUS UNBEKANNT
        </p>
      )}
      <table>
        <thead>
          <tr>
            <th>STATUS</th>
            <th>E-MAIL</th>
            <th>ROLLE</th>
            <th>ZUGANG</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((member) => {
            const presence = presenceByUserId.get(member.userId);
            return (
              <tr
                key={member.userId}
                className={member.active ? "" : "inactive"}
              >
                <td className="members-presence">
                  <span
                    className={
                      presence
                        ? "members-dot members-dot-online"
                        : "members-dot"
                    }
                    aria-hidden="true"
                  />
                  {presenceLabel(presence, presenceConnected)}
                </td>
                <td>
                  {member.email}
                  {member.userId === currentUserId ? <em> · DU</em> : null}
                </td>
                <td>
                  {canManage ? (
                    <select
                      name={`member-role-${member.userId}`}
                      value={member.role}
                      disabled={busy}
                      aria-label={`Rolle von ${member.email}`}
                      onChange={(event) =>
                        void patch(member, {
                          role: event.target.value as Member["role"],
                        })
                      }
                    >
                      <option value="viewer">VIEWER</option>
                      <option value="editor">EDITOR</option>
                      <option value="captain">CAPTAIN</option>
                    </select>
                  ) : (
                    <Badge>{roleLabels[member.role]}</Badge>
                  )}
                </td>
                <td>
                  {canManage ? (
                    <button
                      disabled={busy}
                      onClick={() =>
                        void patch(member, { active: !member.active })
                      }
                    >
                      <ShieldCheck />
                      {member.active ? "AKTIV" : "DEAKTIVIERT"}
                    </button>
                  ) : (
                    <span className="members-access">
                      {member.active ? "AKTIV" : "DEAKTIVIERT"}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {canManage ? (
        <>
          <form
            className="members-invite"
            onSubmit={(event) => {
              event.preventDefault();
              void invite();
            }}
          >
            <label>
              <span className="ui-field-label">
                NEUES MITGLIED <RequiredMark />
              </span>
              <input
                id="invite-email"
                name="invite-email"
                type="email"
                autoComplete="off"
                required
                value={email}
                placeholder="name@team.test"
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <select
              id="invite-role"
              name="invite-role"
              value={role}
              aria-label="Rolle der Einladung"
              onChange={(event) =>
                setRole(event.target.value as Member["role"])
              }
            >
              <option value="viewer">VIEWER</option>
              <option value="editor">EDITOR</option>
              <option value="captain">CAPTAIN</option>
            </select>
            <button type="submit" disabled={busy || !email.trim()}>
              <Mail /> EINLADEN
            </button>
          </form>
          <p className="members-roles">
            VIEWER liest nur · EDITOR pflegt Inhalte · CAPTAIN gibt frei,
            veröffentlicht und verwaltet Zugänge · es gibt immer genau einen
            Captain — ein neuer Captain stuft den bisherigen automatisch zu
            EDITOR zurück
          </p>
        </>
      ) : null}
      <p className="members-status">{status}</p>
    </Dialog>
  );
}
